import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { loadStripe } from '@stripe/stripe-js';

import {
  approveEstimate,
  getCards,
  getEstimateDepositCheckoutSession,
  getEstimateDepositInfo,
} from 'api';

import config from 'config';

import { getApiErrorMessage } from 'helpers/error.helper';

import { SignatureBox } from 'components/ui-kit/signature';
import NoTextCheckbox from 'components/ui-kit/no-text-checkbox';
import ButtonGroup from 'components/ui-kit/button-group';
import Button from 'components/ui-kit/buttons/button';
import Typography from 'components/ui-kit/typography';
import Modal from 'components/ui-kit/modal';

import { useApp, useModal, useToast } from 'hooks';

import * as S from './styles';

const DEPOSIT_REQUIREMENT = { requested: 0, required: 1 };

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const ApproveEstimateModal = ({ id, estimateTitle, lineItems }) => {
  const { hideModal } = useModal();
  const { portal } = useApp();
  const portalId = portal?.id;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [signature, setSignature] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [payDeposit, setPayDeposit] = useState(true);

  const acceptedLineItemIds = (lineItems || [])
    .filter((li) => !li.optionalState || li.optionalState.isAccepted)
    .map((li) => li.id)
    .join(',');

  const { data: depositRes } = useQuery(
    ['estimate-deposit', portalId, id, acceptedLineItemIds],
    getEstimateDepositInfo,
    { enabled: !!portalId, refetchOnWindowFocus: false, retry: false },
  );
  const deposit = depositRes?.data || {
    isRequired: false,
    amount: 0,
    total: 0,
    requirement: DEPOSIT_REQUIREMENT.required,
  };
  const hasDeposit = !!deposit.isRequired && deposit.amount > 0;
  const isMandatory = deposit.requirement === DEPOSIT_REQUIREMENT.required;
  const collectNow = hasDeposit && payDeposit;
  const depositTotal = deposit.total || 0;
  const depositRemaining = Math.max(0, depositTotal - (deposit.amount || 0));

  const { data: cardsRes } = useQuery(
    ['cardsListingQuery', portalId],
    getCards,
    { enabled: !!portalId && hasDeposit, refetchOnWindowFocus: false },
  );
  const cards = cardsRes?.data || [];

  useEffect(() => {
    const firstCard = cardsRes?.data?.[0]?.methodId;
    if (hasDeposit && firstCard) {
      setPaymentMethodId((prev) => prev || firstCard);
    }
  }, [hasDeposit, cardsRes]);

  const noCardForDeposit = collectNow && !cards.length;

  const approveMutation = useMutation(() => (approveEstimate(portalId, {
    id,
    signature,
    lineItems,
    paymentMethodId: collectNow && !noCardForDeposit ? paymentMethodId : undefined,
    payByCheckout: noCardForDeposit,
  })), {
    onSuccess: () => queryClient.invalidateQueries(['appointment', portal?.id, id]),
    onError: (error) => {
      toast.addErrorToast(getApiErrorMessage(error, 'Something went wrong during approval'));
    },
  });

  const redirectToCheckout = async () => {
    const { data: session } = await getEstimateDepositCheckoutSession(portalId, id, acceptedLineItemIds);
    const { sessionId, connectedAccountId } = session || {};

    if (!sessionId || !connectedAccountId) {
      toast.addErrorToast('Company did not connect bank account');
      return;
    }

    const stripe = await loadStripe(config.stripePublicKey, { stripeAccount: connectedAccountId });
    await stripe.redirectToCheckout({ sessionId });
  };

  const sign = async () => {
    try {
      await approveMutation.mutateAsync();

      if (noCardForDeposit) {
        await redirectToCheckout();
        return;
      }

      hideModal();
    } catch {
      // error toast handled in onError; keep the modal open so the customer can retry
    }
  };

  const approveDisabled = !signature
    || approveMutation.isLoading
    || (collectNow && !noCardForDeposit && !paymentMethodId);
  const approveLabel = collectNow ? `Approve & Pay ${formatMoney(deposit.amount)}` : 'Approve';

  return (
    <Modal size="4/10" title="Approve estimate">
      <Typography
        fluidFont={{
          min: 12, max: 16,
        }}
      >
        You are going to approve estimate
        {' '}
        <strong>{estimateTitle}</strong>
        . You won’t be able to undo this action.
        {' '}
        <br />
        You can add your signature.
      </Typography>
      <S.Box>
        <SignatureBox onChanged={(s) => setSignature(s)} />
      </S.Box>

      {hasDeposit && (
        <S.DepositCallout>
          <S.DepositRow>
            <S.DepositLabel>{isMandatory ? 'Deposit due today' : 'Deposit'}</S.DepositLabel>
            <S.DepositValue>{formatMoney(deposit.amount)}</S.DepositValue>
          </S.DepositRow>
          {depositRemaining > 0 && depositTotal > 0 && (
            <S.DepositRow>
              <S.DepositSubLabel>Remaining after approval</S.DepositSubLabel>
              <S.DepositSubValue>{formatMoney(depositRemaining)}</S.DepositSubValue>
            </S.DepositRow>
          )}

          {!isMandatory && (
            <S.CheckRow>
              <NoTextCheckbox checked={payDeposit} onChange={setPayDeposit} size={18} />
              <S.CheckLabel onClick={() => setPayDeposit((v) => !v)}>
                {`Pay ${formatMoney(deposit.amount)} deposit now`}
              </S.CheckLabel>
            </S.CheckRow>
          )}

          {collectNow && (noCardForDeposit ? (
            <S.DepositNote>
              You’ll be redirected to a secure Stripe page to pay the deposit after approval.
            </S.DepositNote>
          ) : (
            <S.CardSelect
              value={paymentMethodId}
              onChange={(e) => setPaymentMethodId(e.target.value)}
            >
              {cards.map((card) => (
                <option key={card.methodId} value={card.methodId}>
                  {`${card.brand || 'Card'} •••• ${card.last4}`}
                </option>
              ))}
            </S.CardSelect>
          ))}

          {hasDeposit && !isMandatory && !payDeposit && (
            <S.DepositNote>
              {`You can pay the ${formatMoney(deposit.amount)} deposit later from this estimate.`}
            </S.DepositNote>
          )}
        </S.DepositCallout>
      )}

      <Typography
        fluidFont={{
          min: 12, max: 16,
        }}
      >
        By signing, you accept this Estimate, associated costs, Privacy Policy and Terms and conditions.
      </Typography>
      <ButtonGroup justifyContent="flex-end" topGap="1rem">
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          color="success"
          onClick={sign}
          isLoading={approveMutation.isLoading}
          disabled={approveDisabled}
        >
          {approveLabel}
        </Button>
      </ButtonGroup>
    </Modal>
  );
};

ApproveEstimateModal.propTypes = {
  id: PropTypes.string.isRequired,
  estimateTitle: PropTypes.string.isRequired,
  lineItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    unitPrice: PropTypes.number.isRequired,
  })).isRequired,
};

export default ApproveEstimateModal;
